window.theme = window.theme || {};
window.slate = window.slate || {};

theme.Filters = (function() {
  var selectors = {
    filterSelection: '#SortTags',
    sortSelection: '#SortBy',
    ajaxContent: '#CollectionAjaxContent',
    paginationLink: '.pagination a'
  };

  function Filters() {
    this.filterSelect = document.querySelector(selectors.filterSelection);
    this.sortSelect = document.querySelector(selectors.sortSelection);
    this.contentEl = document.querySelector(selectors.ajaxContent);
    this.sectionId = this.contentEl && this.contentEl.getAttribute('data-section-id');

    this.default = this._getDefaultSortValue();
    if (this.sortSelect) {
      this.sortSelect.value = this.default;
    }

    this._onFilterChangeHandler = this._onFilterChange.bind(this);
    this._onSortChangeHandler = this._onSortChange.bind(this);
    this._onPaginationClickHandler = this._onPaginationClick.bind(this);
    this._onPopStateHandler = this._onPopState.bind(this);

    if (this.filterSelect) {
      this.filterSelect.addEventListener('change', this._onFilterChangeHandler);
    }
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this._onSortChangeHandler);
    }
    if (this.contentEl) {
      // Pagination links are replaced along with the rest of the content on
      // every AJAX render, so this is bound once via delegation rather than
      // re-bound to each new set of links.
      this.contentEl.addEventListener('click', this._onPaginationClickHandler);
    }
    window.addEventListener('popstate', this._onPopStateHandler);
  }

  Filters.prototype = Object.assign({}, Filters.prototype, {
    _getDefaultSortValue: function() {
      return (
        Shopify.queryParams.sort_by ||
        (this.sortSelect && this.sortSelect.dataset.defaultSort)
      );
    },

    _onFilterChange: function() {
      delete Shopify.queryParams.page;
      var query = new URLSearchParams(Shopify.queryParams).toString();
      this._navigate(this.filterSelect.value + (query ? '?' + query : ''));
    },

    _onSortChange: function() {
      Shopify.queryParams.sort_by = this.sortSelect.value;
      var query = new URLSearchParams(Shopify.queryParams).toString();
      this._navigate(location.pathname + (query ? '?' + query : ''));
    },

    _onPaginationClick: function(evt) {
      var link = evt.target.closest(selectors.paginationLink);
      if (!link) return;

      evt.preventDefault();
      this._navigate(link.href);
    },

    _onPopState: function() {
      this._syncQueryParamsFromLocation();
      this._syncControls();
      this._render(location.href, false);
    },

    // Shopify.queryParams is built once from location.search when
    // theme-core.js first runs, and we mutate it in place across AJAX
    // navigations since there's no real page load to rebuild it. On popstate the browser has
    // already changed location.search itself, so this is the one point it
    // needs to be resynced from the URL rather than mutated further.
    _syncQueryParamsFromLocation: function() {
      var params = {};
      new URLSearchParams(location.search).forEach(function(value, key) {
        params[key] = value;
      });
      Shopify.queryParams = params;
    },

    // The tag/sort <select> elements persist across AJAX renders (only the
    // grid + pagination are swapped), so on popstate their displayed value
    // needs to be brought back in sync with whatever URL was navigated to.
    _syncControls: function() {
      if (this.filterSelect) {
        var currentPath = location.pathname;
        var matchingOption = Array.prototype.find.call(this.filterSelect.options, function(option) {
          try {
            return new URL(option.value, location.origin).pathname === currentPath;
          } catch (err) {
            return false;
          }
        });
        if (matchingOption) this.filterSelect.value = matchingOption.value;
      }

      if (this.sortSelect) {
        this.sortSelect.value = this._getDefaultSortValue();
      }
    },

    _navigate: function(url) {
      history.pushState({}, '', url);
      this._render(url, true);
    },

    _render: function(url, scrollToContent) {
      if (!this.contentEl || !this.sectionId) {
        location.href = url;
        return;
      }

      var self = this;
      this.contentEl.classList.add('loading');

      var separator = url.indexOf('?') === -1 ? '?' : '&';
      fetch(url + separator + 'section_id=' + this.sectionId)
        .then(function(res) {
          if (!res.ok) throw new Error('Collection filter request failed with status ' + res.status);
          return res.text();
        })
        .then(function(html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newContent = doc.querySelector(selectors.ajaxContent);
          if (!newContent) throw new Error('Collection filter response missing content');

          self.contentEl.innerHTML = newContent.innerHTML;
          self.contentEl.classList.remove('loading');

          if (scrollToContent) {
            self.contentEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function() {
          // Fall back to a full navigation if the AJAX update fails, so the
          // grid is never left showing stale/mismatched products.
          location.href = url;
        });
    },

    onUnload: function() {
      if (this.filterSelect) {
        this.filterSelect.removeEventListener('change', this._onFilterChangeHandler);
      }
      if (this.sortSelect) {
        this.sortSelect.removeEventListener('change', this._onSortChangeHandler);
      }
      if (this.contentEl) {
        this.contentEl.removeEventListener('click', this._onPaginationClickHandler);
      }
      window.removeEventListener('popstate', this._onPopStateHandler);
    }
  });

  return Filters;
})();

theme.CollectionsList = (function() {
  function debounce(delay, fn) {
    var timer;
    return function() {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function() {
        fn.apply(null, args);
      }, delay);
    };
  }

  function CollectionsList(container) {
    this.container = container;
    var stretchImage = container.hasAttribute('data-stretch-image');

    // Early return if 'secondary image layout' is enabled
    if (stretchImage) return;

    this.namespace = container.getAttribute('data-section-id');
    var self = this;

    self._collectionListFix();
    this._onResize = debounce(250, function() {
      self._collectionListFix();
    });
    window.addEventListener('resize', this._onResize);
  }

  CollectionsList.prototype = Object.assign({}, CollectionsList.prototype, {
    onUnload: function() {
      window.removeEventListener('resize', this._onResize);
    },

    _collectionListFix: function() {
      var gridEl = this.container.querySelector('.grid');
      var numberRows = gridEl ? parseInt(gridEl.dataset.numberRows, 10) : 0;
      var featureCards = this.container.querySelectorAll('.featured-card');

      // We can exit if 'Use secondary image layout' is enabled
      var hasStretchImage = Array.prototype.some.call(featureCards, function(el) {
        return el.hasAttribute('data-stretch-image');
      });
      if (hasStretchImage) return;

      // Go through each of the rows
      for (var i = 0; i < numberRows; i++) {
        var maxWrapperHeight = 0;
        var maxHeaderHeight = 0;
        var currentRow = Array.prototype.filter.call(featureCards, function(el) {
          return el.getAttribute('data-row-number') === String(i + 1);
        });
        var cardHeaders = [];

        // Find the max heights for each row
        currentRow.forEach(function(row) {
          var cardTitle = row.querySelector('.featured-card__title');
          var cardAction = row.querySelector('.featured-card__action');
          var cardImageWrapper = row.querySelector('.featured-card__image-wrapper');
          var cardHeader = row.querySelector('.featured-card__header');
          if (cardHeader) cardHeaders.push(cardHeader);

          var headerHeight =
            (cardTitle ? cardTitle.offsetHeight : 0) +
            (cardAction ? cardAction.offsetHeight : 0) +
            65;
          var wrapperHeight = cardImageWrapper ? cardImageWrapper.offsetHeight : 0;
          if (headerHeight > maxHeaderHeight) {
            maxHeaderHeight = headerHeight;
          }
          if (wrapperHeight > maxWrapperHeight) {
            maxWrapperHeight = wrapperHeight;
          }
        });

        // Set the heights of the headers and cards for this row with padding
        cardHeaders.forEach(function(header) {
          header.style.height = maxHeaderHeight + 'px';
        });
        currentRow.forEach(function(row) {
          row.style.height = maxWrapperHeight + maxHeaderHeight + 40 + 'px';
        });
      }
    }
  });

  return CollectionsList;
})();
